import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, Save, X } from 'lucide-react';
import { adminApi } from '../../services/api';
import { annoncerErreur, annoncerSucces, EtatErreur, SqueletteListe, confirmer } from '../../etats';

/* ═══════════════════════════════════════════════════════════════════════
   LE REGARD DU COACH — le même questionnaire, rempli par l'expert

   Le score global est pour moitié celui du coach (`interpretation.combiner`),
   et il n'avait aucun écran pour répondre : le questionnaire n'existait que
   dans l'espace de l'organisation. Il s'ouvre ici quand tous les
   participants ont terminé — le serveur le refuse avant.

   Domaine par domaine, enregistré à chaque étape : le coach reprend où il
   s'est arrêté. Seuls la question et la réponse partent ; les points sont
   relus dans le référentiel côté serveur.
   ═══════════════════════════════════════════════════════════════════════ */

export default function QuestionnaireCoach({ diagnostic, dejaRepondu, onClose, onEnregistre }) {
  const [structure, setStructure] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [reponses, setReponses] = useState(() => ({ ...(dejaRepondu || {}) }));
  const [enregistrees, setEnregistrees] = useState(() => ({ ...(dejaRepondu || {}) }));
  const [index, setIndex] = useState(0);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(() => {
    setErreur(null);
    adminApi.getStructure()
      .then((d) => setStructure((Array.isArray(d) ? d : []).filter(
        (dom) => (dom.subdomains || []).some((sd) => (sd.questions || []).length))))
      .catch((e) => setErreur(e));
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const domaine = structure?.[index];
  const questionsDu = (dom) => (dom?.subdomains || []).flatMap((sd) => sd.questions || []);
  const total = useMemo(() => (structure || []).reduce((n, d) => n + questionsDu(d).length, 0), [structure]);
  const repondues = Object.keys(reponses).length;
  const modifie = Object.keys(reponses).some((q) => reponses[q] !== enregistrees[q]);

  const fermer = async () => {
    if (modifie && !await confirmer('Fermer sans enregistrer ? Les réponses de ce domaine non enregistrées seront perdues.')) return;
    onClose();
  };

  useEffect(() => {
    const touche = (e) => { if (e.key === 'Escape') fermer(); };
    document.addEventListener('keydown', touche);
    return () => document.removeEventListener('keydown', touche);
  });

  const enregistrer = async (suivant) => {
    const aEnvoyer = questionsDu(domaine)
      .filter((q) => reponses[q.id] && reponses[q.id] !== enregistrees[q.id])
      .map((q) => ({ question_id: q.id, reponse_id: reponses[q.id] }));
    if (aEnvoyer.length) {
      setEnvoi(true);
      try {
        await adminApi.repondreDiagnostic(diagnostic.id, aEnvoyer);
        setEnregistrees((prev) => {
          const maj = { ...prev };
          aEnvoyer.forEach((r) => { maj[r.question_id] = r.reponse_id; });
          return maj;
        });
        onEnregistre?.();
      } catch (e) {
        annoncerErreur(e.message || 'Vos réponses n’ont pas pu être enregistrées.');
        setEnvoi(false);
        return;
      }
      setEnvoi(false);
    }
    if (suivant === 'fin') {
      annoncerSucces('Votre regard est enregistré. Vous pouvez clôturer le diagnostic.');
      onClose();
    } else if (typeof suivant === 'number') {
      setIndex(suivant);
    }
  };

  const dernier = structure && index === structure.length - 1;

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/50 sm:p-4 flex items-stretch justify-center"
         role="dialog" aria-modal="true" aria-label="Répondre au diagnostic">
      <div className="w-full max-w-5xl bg-[var(--bg-card)] sm:rounded-cadre shadow-carte flex flex-col overflow-hidden">
        <header className="flex items-center gap-3 px-5 py-4 border-b border-[var(--border)]">
          <div className="min-w-0 flex-1">
            <p className="surtitre text-[var(--text-muted)]">
              Votre regard de coach · diagnostic {{ initial: 'initial', mi_parcours: 'à mi-parcours', final: 'final' }[diagnostic.type_diagnostic] || diagnostic.type_diagnostic}
            </p>
            <p className="text-[13px] text-[var(--text-muted)] font-semibold">{repondues} / {total || '…'} questions répondues</p>
          </div>
          <button type="button" onClick={fermer} className="act act-icone act-contour" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </header>

        {erreur ? (
          <div className="p-6"><EtatErreur erreur={erreur} onReessayer={charger} /></div>
        ) : !structure ? (
          <div className="p-6"><SqueletteListe lignes={5} /></div>
        ) : structure.length === 0 ? (
          <p className="p-10 text-center text-sm text-[var(--text-muted)]">Le référentiel ne contient encore aucune question.</p>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
            {/* Les domaines, et ce qui y reste à faire */}
            <nav className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-[var(--border)] overflow-x-auto lg:overflow-y-auto"
                 aria-label="Domaines">
              <ol className="flex lg:flex-col gap-1 p-3">
                {structure.map((dom, i) => {
                  const qs = questionsDu(dom);
                  const faites = qs.filter((q) => enregistrees[q.id]).length;
                  return (
                    <li key={dom.id} className="shrink-0">
                      <button type="button" onClick={() => enregistrer(i)} disabled={envoi}
                              aria-current={i === index ? 'step' : undefined}
                              className={`w-full text-left px-3 py-2 rounded-pastille text-[13px] font-semibold flex items-center gap-2 ${i === index
                                ? 'bg-[var(--accent)]/10 text-[var(--accent-texte)]'
                                : 'text-[var(--text-main)] hover:bg-[var(--bg-main)]'}`}>
                        {faites === qs.length
                          ? <CheckCircle2 className="w-4 h-4 shrink-0 text-[var(--accent-texte)]" />
                          : <span className="w-4 text-center text-[11px] tabular-nums text-[var(--text-muted)]">{i + 1}</span>}
                        <span className="min-w-0 truncate">{dom.nom}</span>
                        <span className="ml-auto text-[11px] tabular-nums text-[var(--text-muted)]">{faites}/{qs.length}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>

            <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6">
              <h3 className="text-xl font-black text-[var(--text-bold)] mb-1">{domaine.nom}</h3>
              {domaine.description && <p className="text-[13px] text-[var(--text-muted)] mb-4">{domaine.description}</p>}
              {(domaine.subdomains || []).filter((sd) => (sd.questions || []).length).map((sd) => (
                <section key={sd.id} className="mt-5">
                  <p className="surtitre text-[var(--text-muted)] mb-2">{sd.nom}</p>
                  <ol className="space-y-4">
                    {sd.questions.map((q) => (
                      <li key={q.id}>
                        <fieldset>
                          <legend className="text-[14px] font-semibold text-[var(--text-bold)] mb-2">{q.texte}</legend>
                          <div className="grid gap-1.5">
                            {[...(q.reponses || [])].sort((a, b) => a.points - b.points).map((r) => (
                              <label key={r.id}
                                     className={`flex items-start gap-3 px-3 py-2 rounded-pastille border cursor-pointer text-[13px] ${reponses[q.id] === r.id
                                       ? 'border-[var(--accent)] bg-[var(--accent)]/10'
                                       : 'border-[var(--border)] hover:bg-[var(--bg-main)]'}`}>
                                <input type="radio" name={`q-${q.id}`} checked={reponses[q.id] === r.id}
                                       onChange={() => setReponses((prev) => ({ ...prev, [q.id]: r.id }))}
                                       className="mt-0.5 accent-[var(--accent)]" />
                                <span className="flex-1">{r.label}</span>
                                <span className="text-[11px] font-semibold text-[var(--text-muted)] tabular-nums">{r.points}/5</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          </div>
        )}

        {structure?.length > 0 && (
          <footer className="flex items-center gap-2 px-5 py-4 border-t border-[var(--border)]">
            <button type="button" onClick={() => enregistrer(index - 1)} disabled={envoi || index === 0}
                    className="act act-contour">
              <ChevronLeft className="w-4 h-4" /> Précédent
            </button>
            <span className="mr-auto" />
            {envoi && <Loader2 className="w-4 h-4 animate-spin text-[var(--text-muted)]" aria-label="Enregistrement…" />}
            {dernier ? (
              <button type="button" onClick={() => enregistrer('fin')} disabled={envoi} aria-busy={envoi || undefined} className="act">
                <Save className="w-4 h-4" /> Enregistrer et terminer
              </button>
            ) : (
              <button type="button" onClick={() => enregistrer(index + 1)} disabled={envoi} aria-busy={envoi || undefined} className="act">
                Enregistrer et continuer <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
